// client/src/components/services_tester/common/CompactInfrastructureCard.jsx
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Button,
  Tooltip,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Info,
} from '@mui/icons-material';

// ============================================================================
// Хелперы
// ============================================================================

const getStatusColor = (status) => {
  if (!status) return 'default';
  switch (status.status) {
    case 'healthy':
    case 'connected':
    case 'success':
      return 'success';
    case 'error':
    case 'unhealthy':
      return 'error';
    case 'warning':
      return 'warning';
    default:
      return 'default';
  }
};

const getStatusText = (status) => {
  if (!status) return 'unknown';
  return status.status || 'unknown';
};

const getStatusIcon = (status) => {
  if (!status) return <Info sx={{ fontSize: 12 }} />;
  switch (status.status) {
    case 'healthy':
    case 'connected':
    case 'success':
      return <CheckCircle sx={{ fontSize: 12 }} />;
    case 'error':
    case 'unhealthy':
      return <ErrorIcon sx={{ fontSize: 12 }} />;
    case 'warning':
      return <Warning sx={{ fontSize: 12 }} />;
    default:
      return <Info sx={{ fontSize: 12 }} />;
  }
};

// ============================================================================
// Компонент
// ============================================================================

export default function CompactInfrastructureCard({
  title,
  icon,
  status,
  onCheck,
  onDetailed,
  loading = false,
  testLabel,
  onTest,
  adminTestLabel,
  onAdminTest,
  adminLoading = false,
  // ✅ Добавлены: были в JSX, но не в props — отсюда ReferenceError
  detailedTestLabel,
  onDetailedTest,
  disabled = false,
  size = 'small',
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        opacity: disabled ? 0.6 : 1,
        borderRadius: 1,
        transition: 'border-color 0.2s',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <CardContent sx={{ p: 0.75, '&:last-child': { pb: 0.75 } }}>
        {/* Header: icon + title + status */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.5,
            mb: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
            {React.cloneElement(icon, { sx: { fontSize: 16 } })}
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {loading && <CircularProgress size={12} />}
            <Chip
              icon={getStatusIcon(status)}
              label={getStatusText(status)}
              size="small"
              color={getStatusColor(status)}
              variant={!status || status?.status === 'unknown' ? 'outlined' : 'filled'}
              sx={{
                height: 18,
                fontSize: '0.6rem',
                '& .MuiChip-label': { px: 0.5 },
                '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
              }}
            />
          </Box>
        </Box>

        {/* Кнопки */}
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={onCheck}
            disabled={loading || disabled}
            sx={{
              minWidth: 'auto',
              px: 0.75,
              py: 0,
              height: 22,
              fontSize: '0.6rem',
              textTransform: 'none',
              lineHeight: 1,
            }}
          >
            Проверить
          </Button>

          {onDetailed && (
            <Button
              size="small"
              variant="outlined"
              onClick={onDetailed}
              disabled={loading || !status || disabled}
              sx={{
                minWidth: 'auto',
                px: 0.75,
                py: 0,
                height: 22,
                fontSize: '0.6rem',
                textTransform: 'none',
                lineHeight: 1,
              }}
            >
              Детали
            </Button>
          )}

          {onTest && testLabel && (
            <Button
              size="small"
              variant="outlined"
              onClick={onTest}
              disabled={loading || disabled}
              sx={{
                minWidth: 'auto',
                px: 0.75,
                py: 0,
                height: 22,
                fontSize: '0.6rem',
                textTransform: 'none',
                lineHeight: 1,
              }}
            >
              {testLabel}
            </Button>
          )}

          {onAdminTest && adminTestLabel && (
            <Button
              size="small"
              variant="outlined"
              onClick={onAdminTest}
              disabled={adminLoading || disabled}
              sx={{
                minWidth: 'auto',
                px: 0.75,
                py: 0,
                height: 22,
                fontSize: '0.6rem',
                textTransform: 'none',
                lineHeight: 1,
              }}
            >
              {adminTestLabel}
            </Button>
          )}

          {onDetailedTest && detailedTestLabel && (
            <Button
              size="small"
              variant="outlined"
              onClick={onDetailedTest}
              disabled={loading || disabled}
              sx={{
                minWidth: 'auto',
                px: 0.75,
                py: 0,
                height: 22,
                fontSize: '0.6rem',
                textTransform: 'none',
                lineHeight: 1,
              }}
            >
              {detailedTestLabel}
            </Button>
          )}
        </Stack>

        {/* Сообщение (1 строка) */}
        {status?.message && (
          <Tooltip title={status.message} arrow>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                mt: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.6rem',
                lineHeight: 1.2,
              }}
            >
              {status.message}
            </Typography>
          </Tooltip>
        )}
      </CardContent>
    </Card>
  );
}
